import { Box } from "@mui/material";
import { ExternalLink, Pencil, Percent, Unlink } from "lucide-react";
import type { FC } from "react";
import { useNavigate } from "react-router-dom";
import { SolutionItemActionRow } from "web_myplann/Goals/Components/SolutionItemActionRow";
import {
	type GoalPortfolioResponseDTO,
	GoalPortfolioTypeEnum,
} from "web_myplann/Infrastructure/Api/Api";
import { Routing } from "web_myplann/Infrastructure/Routing/Routing";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import { MyPlannDrawer } from "web_myplann/Shared/Components/MyPlannDrawer";
import {
	SOLUTION_ACTION_ALLOCATION_ICON_BG,
	SOLUTION_ACTION_ALLOCATION_ICON_COLOR,
	SOLUTION_ACTION_DETAIL_ICON_BG,
	SOLUTION_ACTION_DETAIL_ICON_COLOR,
	SOLUTION_ACTION_UNPIN_ICON_BG,
	SOLUTION_ACTION_UNPIN_ICON_COLOR,
	SOLUTION_ACTION_UPDATE_ICON_BG,
	SOLUTION_ACTION_UPDATE_ICON_COLOR,
} from "web_myplann/Utils/constants";

type Props = {
	open: boolean;
	portfolio: GoalPortfolioResponseDTO;
	onClose: () => void;
	onUnpin: (portfolioId: number) => void;
	onEditAllocation?: (portfolio: GoalPortfolioResponseDTO) => void;
};

export const SolutionItemActionDrawer: FC<Props> = ({
	open,
	portfolio,
	onClose,
	onUnpin,
	onEditAllocation,
}) => {
	const { t } = useResource();
	const navigate = useNavigate();

	const handleDetail = () => {
		navigate(
			Routing.Portfolio.PortfolioDetail.path(portfolio.portfolioId.toString()),
		);
		onClose();
	};

	const handleUpdateValue = () => {
		// TODO: open update-value form
		onClose();
	};

	const handleEditAllocation = () => {
		onEditAllocation?.(portfolio);
		onClose();
	};

	const handleUnpin = () => {
		onUnpin(portfolio.portfolioId);
		onClose();
	};

	return (
		<MyPlannDrawer
			anchor="bottom"
			open={open}
			onClose={onClose}
			title={portfolio.name ?? ""}
			closeButton
		>
			<Box pb={1}>
				<SolutionItemActionRow
					onClick={handleDetail}
					Icon={ExternalLink}
					iconBg={SOLUTION_ACTION_DETAIL_ICON_BG}
					iconColor={SOLUTION_ACTION_DETAIL_ICON_COLOR}
					label={t(Resources.Goals.Detail.Solution.Actions.detail)}
					hasDivider
				/>
				<SolutionItemActionRow
					onClick={handleUpdateValue}
					Icon={Pencil}
					iconBg={SOLUTION_ACTION_UPDATE_ICON_BG}
					iconColor={SOLUTION_ACTION_UPDATE_ICON_COLOR}
					label={t(Resources.Goals.Detail.Solution.Actions.updateValue)}
					hasDivider
				/>
				{portfolio.type !== GoalPortfolioTypeEnum.SetAside && (
					<SolutionItemActionRow
						onClick={handleEditAllocation}
						Icon={Percent}
						iconBg={SOLUTION_ACTION_ALLOCATION_ICON_BG}
						iconColor={SOLUTION_ACTION_ALLOCATION_ICON_COLOR}
						label={t(Resources.Goals.Detail.Solution.Actions.editAllocation)}
						description={t(
							Resources.Goals.Detail.Solution.Actions.editAllocationDescription,
							{ percent: portfolio.usagePercent },
						)}
						hasDivider
					/>
				)}
				<SolutionItemActionRow
					onClick={handleUnpin}
					Icon={Unlink}
					iconBg={SOLUTION_ACTION_UNPIN_ICON_BG}
					iconColor={SOLUTION_ACTION_UNPIN_ICON_COLOR}
					label={t(Resources.Goals.Detail.Solution.Actions.unpin)}
				/>
			</Box>
		</MyPlannDrawer>
	);
};
